import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { ArrowLeftCircle } from "react-bootstrap-icons";
import AxiosInstance from "../AxiosInstance";
import Layout from "./Layout";
import { Container, Row, Col } from "react-bootstrap";
import ToastNotification from '../ToastNotification'; // Import the Toast component

const DetailsPage = () => {
  const { state } = useLocation();
  const { username, startDate, endDate, status } = state;
  const [details, setDetails] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workDates, setWorkDates] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [customReason, setCustomReason] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isProcessed, setIsProcessed] = useState(false);

  const [leaveDates, setLeaveDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [leaveWorkHours, setLeaveWorkHours] = useState(0); 


  const toastRef = useRef(null);

  useEffect(() => {
    fetchDetails();
  }, [username, startDate, endDate]);

  const fetchDetails = async () => {
    try {
      const response = await AxiosInstance.post("/admin/details", {
        username,
        start_date: startDate,
        end_date: endDate,
      });
  
      const { details, first_name, last_name, middle_name, leaveDates: fetchedLeaveDates, holidayDates: fetchedHolidayDates, leaveWorkHours: fetchedLeaveWorkHours } = response.data;
  
      if (details.length === 0) {
      } else {
        const projectNames = [
          ...new Set(details.flatMap((row) => Object.keys(row).filter((key) => key !== "date"))),
        ];
        const dates = details.map((row) => row.date);
  
        setDetails(details);
        setProjects(projectNames);
        setWorkDates(dates);
        setFirstName(first_name || "");
        setLastName(last_name || "");
        setMiddleName(middle_name || "");
  
        //Setting leave and holiday data
        setLeaveDates(fetchedLeaveDates || []);
        setHolidayDates(fetchedHolidayDates || []);
        setLeaveWorkHours(fetchedLeaveWorkHours || 0);
      }
    } catch (err) {
      toastRef.current?.showToast("Error fetching details.", "danger");
    }
  };
  

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString("en-GB", { weekday: "long" });
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    return (
      <>
        {dayName} <br /> {formattedDate}
      </>
    );
  };

  const capitalizeName = (name) => {
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleAccept = async (username, startDate, endDate) => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post('/admin/accept', {
        username,
        start_date: startDate,
        end_date: endDate  
      });

      if (response.data.message === 'Work hours accepted successfully.') {
        toastRef.current.showToast(response.data.message, "success"); // Show success toast
        setIsProcessed(true);
        fetchDetails();  
      }
    } catch (err) {
      toastRef.current.showToast('Error accepting work hour: ' + err.message, "danger"); // Show error toast
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const rejection = rejectionReason === 'Other' ? customReason : rejectionReason;

    if (!rejection.trim()) {
      toastRef.current.showToast('Please provide a rejection reason.', "warning"); // Show warning toast
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await AxiosInstance.post('/admin/reject', {
        username: selectedEmployee.username,
        rejected_reason: rejection,
        start_date: startDate,
        end_date: endDate,
      });

      if (response.data.message === "Work hours rejected successfully.") {
        toastRef.current.showToast(response.data.message, "danger"); // Show rejection success toast
        setShowModal(false);
        setCustomReason("");
        setIsProcessed(true);
        fetchDetails();  
      }
    } catch (err) {
      toastRef.current.showToast('Error rejecting work hour: ' + err.message, "danger"); // Show error toast
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = () => {
    setSelectedEmployee({ username, startDate, endDate });
    setShowModal(true);
  };

  useEffect(() => {
    const fetchRejectionReasons = async () => {
      try {
        const response = await AxiosInstance.get("/common/timesheetReasons");
        setRejectionReasons(response.data);
      } catch (err) {
        toastRef.current?.showToast("Error fetching rejection reasons.", "danger");
      }
    };

    fetchRejectionReasons();
  }, []);

  return (
    <Layout heading="Work Hour">
      <Container fluid>
        <Row className="mb-3">
          <Col xs={12}>
            <Button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
              <ArrowLeftCircle />
            </Button>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col xs={12}>
            <h3 style={{ fontSize: "20px", fontWeight: "bold", color: "grey", textAlign: "left" }}>
              Employee Name: {`${capitalizeName(firstName)} ${middleName ? capitalizeName(middleName) + ' ' : ''}${capitalizeName(lastName)}`}
            </h3>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {details.length > 0 ? (
              <>
                {error && <div className="alert alert-danger">{error}</div>}

                {/* 🌐 Mobile View: Scrollable Table */}
                <div className="table-responsive d-block d-md-none" style={{ maxHeight: "300px", overflowX: "auto", overflowY: "auto" }}>
                  <Table striped bordered hover className="table-hover text-center align-middle table-striped">
                    <thead className="table-primary">
                      <tr>
                        <th>Project</th>
                        {workDates.map((workDate, index) => (
                          <th key={index} style={{ whiteSpace: "nowrap", textAlign: "left", minWidth: "90px" }}>
                            {formatDate(workDate)}
                          </th>
                        ))}
                        <th>Total Hours</th>
                      </tr>
                    </thead>
                   <tbody>
                    {projects.map((project, index) => {
                      const projectTotal = workDates.reduce((sum, workDate) => {
                        const workHours = details.find((row) => row.date === workDate)?.[project] || "0";
                        return sum + Number(workHours);
                      }, 0);

                      return (
                        <tr key={index}>
                          <td>{project}</td>
                          {workDates.map((workDate, dateIndex) => {
                            const workHours = details.find((row) => row.date === workDate)?.[project] || "0";
                            return <td key={dateIndex}>{workHours}</td>;
                          })}
                          <td style={{ fontWeight: "bold", textAlign: "left" }}>{projectTotal}</td>
                        </tr>
                      );
                    })}

                    {(leaveDates.length > 0 || holidayDates.length > 0) && (
                      <tr className="table-warning">
                        <td className="text-center">Leave / Holiday</td>
                        {workDates.map((workDate, index) => {
                          const isLeaveOrHoliday = leaveDates.includes(workDate) || holidayDates.includes(workDate);
                          return (
                            <td key={index} className="text-center">
                              {isLeaveOrHoliday ? leaveWorkHours : ""}
                            </td>
                          );
                        })}
                        <td style={{ fontWeight: "bold", textAlign: "left" }}>
                        {
                          workDates.reduce((sum, date) => {
                            const isLeaveOrHoliday = leaveDates.includes(date) || holidayDates.includes(date);
                            return sum + (isLeaveOrHoliday ? leaveWorkHours : 0);
                          }, 0)
                        }
                      </td>
                      </tr>
                    )}

                    {/* ⭐ Grand Total Row ⭐ */}
                    <tr style={{ backgroundColor: "#f0f0f0" }}>
                      <td className="text-end fw-bold" colSpan={workDates.length + 1}>
                        Grand Total:
                      </td>
                      <td className="fw-bold">
                        {
                          workDates.reduce((grandTotal, date) => {
                            const isLeaveOrHoliday = leaveDates.includes(date) || holidayDates.includes(date);
                            const leaveOrHolidayHours = isLeaveOrHoliday ? leaveWorkHours : 0;

                            const totalProjectHours = projects.reduce((sum, proj) => {
                              const workHours = details.find((row) => row.date === date)?.[proj] || "0";
                              return sum + Number(workHours);
                            }, 0);

                            return grandTotal + totalProjectHours + leaveOrHolidayHours;
                          }, 0)
                        }
                      </td>
                    </tr>
                  </tbody>

                  </Table>
                </div>

                {/* 🖥️ Desktop View: Full Width Table */}
                <div className="table-responsive d-none d-md-block">
                  <Table striped bordered hover className="table-hover text-center align-middle table-striped">
                    <thead className="table-primary">
                      <tr>
                        <th>Project</th>
                        {workDates.map((workDate, index) => (
                          <th key={index} style={{ whiteSpace: "nowrap", textAlign: "left" }}>
                            {formatDate(workDate)}
                          </th>
                        ))}
                        <th>Total Hours</th>
                      </tr>
                    </thead>
                   <tbody>
                      {projects.map((project, index) => {
                        const projectTotal = workDates.reduce((sum, workDate) => {
                          const workHours = details.find((row) => row.date === workDate)?.[project] || "0";
                          return sum + Number(workHours);
                        }, 0);

                        return (
                          <tr key={index}>
                            <td>{project}</td>
                            {workDates.map((workDate, dateIndex) => {
                              const workHours = details.find((row) => row.date === workDate)?.[project] || "0";
                              return <td key={dateIndex}>{workHours}</td>;
                            })}
                            <td style={{ fontWeight: "bold", textAlign: "left" }}>{projectTotal}</td>
                          </tr>
                        );
                      })}

                      {(leaveDates.length > 0 || holidayDates.length > 0) && (
                        <tr className="table-warning">
                          <td className="text-center">Leave / Holiday</td>
                          {workDates.map((workDate, index) => {
                            const isLeaveOrHoliday = leaveDates.includes(workDate) || holidayDates.includes(workDate);
                            return (
                              <td key={index} className="text-center">
                                {isLeaveOrHoliday ? leaveWorkHours : ""}
                              </td>
                            );
                          })}
                         <td style={{ fontWeight: "bold", textAlign: "left" }}>
                            {
                              workDates.reduce((sum, date) => {
                                const isLeaveOrHoliday = leaveDates.includes(date) || holidayDates.includes(date);
                                return sum + (isLeaveOrHoliday ? leaveWorkHours : 0);
                              }, 0)
                            }
                          </td>

                        </tr>
                      )}

                      {/* ⭐ Grand Total Row ⭐ */}
                      <tr style={{ backgroundColor: "#f0f0f0" }}>
                        <td className="text-end fw-bold" colSpan={workDates.length + 1}>
                          Grand Total:
                        </td>
                        <td className="fw-bold">
                          {
                            workDates.reduce((grandTotal, date) => {
                              const isLeaveOrHoliday = leaveDates.includes(date) || holidayDates.includes(date);
                              const leaveOrHolidayHours = isLeaveOrHoliday ? leaveWorkHours : 0;

                              const totalProjectHours = projects.reduce((sum, proj) => {
                                const workHours = details.find((row) => row.date === date)?.[proj] || "0";
                                return sum + Number(workHours);
                              }, 0);

                              return grandTotal + totalProjectHours + leaveOrHolidayHours;
                            }, 0)
                          }
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* 🟡 Action Buttons for Pending Status */}
                {status === "pending" && !isProcessed && (
                  <>
                    <Button className="btn btn-success me-2" variant="success" onClick={() => handleAccept(username, startDate, endDate)} disabled={loading}>
                      Approve
                    </Button>
                    <Button className="btn btn-danger" variant="danger" onClick={handleShowModal} disabled={loading}>
                      Reject
                    </Button>
                  </>
                )}
              </>
            ) : (
              <p>No work hours available for the selected date range.</p>
            )}
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Work Hour</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="rejectionReason">
            <Form.Label>Rejection Reason</Form.Label>
            <Form.Control as="select" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}>
              <option value="">Select Reason</option>
              {rejectionReasons.map((reason) => (
                <option key={reason.id} value={reason.reason}>{reason.reason}</option>
              ))}
            </Form.Control>
          </Form.Group>
          {rejectionReason === "Other" && (
            <Form.Group controlId="customReason">
              <Form.Label>Enter Custom Reason</Form.Label>
              <Form.Control type="text" value={customReason} onChange={(e) => setCustomReason(e.target.value)} placeholder="Enter custom reason" />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          <Button variant="danger" onClick={handleReject}>Reject</Button>
        </Modal.Footer>
      </Modal>

      <ToastNotification ref={toastRef} /> {/* Add Toast component here */}
    </Layout>
  );
};

export default DetailsPage;
